import { Injectable, isDevMode, signal } from '@angular/core';
import { itemData } from '../data/poe2/item-data';
import { manualItemData } from '../data/poe2/item-data-manual';
import { FlatItemData, UniqueItem, ItemData, UniqueItemDetails, GearData, GearValuesData, ItemDescription, JewelleryData, JewelleryValuesData, NinjaBaseTypesData, DivCard, GemData, FlaskData, Mod, ModSlot, ModTag, ModItemCategory, TieredMod, ModTierType, AffixType } from '../models/item-data-models';
import { GemRewardQuest, QuestReward, VendorGemEntry, Zone } from '../models/extra-models';
import { uniquesList } from '../data/poe2/uniques-list-data';
import { manualUniquesList } from '../data/poe2/uniques-list-data-manual';
import { currencyValuesManual, uniquesValuesManual } from '../data/poe2/currency-values-data-manual';
import { HttpClient } from '@angular/common/http';
import { uniqueBossDropsData } from '../data/poe2/uniques-drop-list-data';
import { classSpecificUniques } from '../data/poe2/class-specific-uniques';
import { armourByDefenceType } from '../data/poe2/armour-by-defence-type';
import { classes, classPresetTexts } from '../data/poe2/classes';
import { minimalCurrencyTierListData, minimalUniqueTierListData } from '../data/poe2/tier-list-data-manual';
import { tierListItemTypeManual } from '../data/poe2/tier-list-item-type-manual';
import { Presets } from "../models/filter-models";
import { EndgameContent, JewelType, ModDomain, ModItemCategoryType } from "../models/enums";
import { ArmourType, WeaponType } from "../models/enums";
import { game, poe1, poe2 } from '../../game';
import { presetEarlyCampaign1, presetEndGame80, presetLateCampaignEarlyMaps60, presetMidMaps70, presetStrict90, presetVeryStrict95 } from '../data/poe2/filter-presets';
import { divinationCardsDataManual } from '../data/poe2/div-card-data-manual';
import { transfiguredGems } from '../data/poe2/transfigured-gems';
import { heistLocations, heistRewards } from '../data/poe2/heist-data';
import { cantHaveQualityItemClasses, cantHaveSocketsItemClasses, cheapCurrency, currencyTypesNotDroppable, currencyTypesToHide, excludedChanceBaseItemClasses, hardCodedChanceBases, itemColors, minimalCharms, nonDroppableUniqueBossTypes, rareOnlySpecialJewellery, restrictedDropTypesToTreatAsRegular, ruthlessDisabledCurrencyItems, ruthlessDisabledCurrencyTypes, ruthlessOnlyCurrencyItems, stackableCurrency } from '../data/poe2/item-lists';
import { importDates } from '../data/poe2/import-dates';
import { lowValueDivCards, veryLowValueDivCards } from '../data/poe2/div-card-values-data-manual';
import { divCardRewardsData } from '../data/poe2/div-card-rewards-data';
import { restrictedDropData } from '../data/poe2/restricted-drop-data';
import { modTierData } from '../data/poe2/mod-tier-data';
import { modSlots } from '../data/poe2/mod-slots';
import { modTags } from '../data/poe2/mod-tags';
import { modItemCategories } from '../data/poe2/mod-item-categories';
import { syndicateMembers } from '../data/poe2/syndicate-members';
import { areas } from '../data/poe2/areas';
import { clusterJewelTiers } from '../data/poe2/cluster-jewels';
import { clusterJewelEnchantments } from '../data/poe2/cluster-jewel-enchantments';
import { gearTierData } from '../data/poe2/gear-tier-list-data';
import { jewelleryTierData } from '../data/poe2/jewellery-tier-list-data';
import { equipmentData } from '../data/poe2/equipment-tier-list-data';
import { ModMatcherService } from './mod-matcher.service';
import { currencyValuesFixed } from '../data/poe2/currency-values-fixed-data';
import { uniqueValuesFixed } from '../data/poe2/unique-values-fixed-data';
import { presetMidCampaign30 } from '../data/poe2/filter-presets';
import { ruthlessPresetEarlyCampaign1, ruthlessPresetEndGame80, ruthlessPresetLateCampaignEarlyMaps60, ruthlessPresetMidCampaign30, ruthlessPresetMidMaps70, ruthlessPresetStrict90, ruthlessPresetVeryStrict95 } from '../data/poe2/filter-presets-ruthless';
import { descriptions } from '../data/descriptions';
import { scarabBaseTypeToContent, scarabContentTypes } from '../data/poe2/scarabs';
import { decodeDivCardReward, DivCardReward } from '../models/div-card-reward';

function parseUniqueListItem(itemStr: string, category: string): UniqueItem {
  const parts = itemStr.split('|');
  const unique = {
    name: parts[0],
    category,
    baseType: parts[1],
    image: parts[2],
    dustValue: parts[3] ? parseFloat(parts[3]) : undefined,
    slots: parts[4] ? parseInt(parts[4]) : undefined,
    uniqueTier: parts[5] !== undefined && parts[5] !== '' ? parseInt(parts[5], 10) : undefined,
  } as UniqueItem;

  if (unique.slots && unique.dustValue) {
    unique.dustValuePerSlot = Math.round(unique.dustValue / unique.slots);
  }

  return unique;
}

function normalizeUniqueDetails(data: (UniqueItemDetails & { dustTier?: number })[]): UniqueItemDetails[] {
  return data.map(u => ({
    ...u,
    uniqueTier: u.uniqueTier ?? u.dustTier,
  }));
}

const ONE_HAND_WEAPON_TYPES = [
  WeaponType.OneHandSwords,
  WeaponType.OneHandAxes,
  WeaponType.OneHandMaces,
  WeaponType.Daggers,
  WeaponType.RuneDaggers,
  WeaponType.Claws,
  WeaponType.Sceptres,
  WeaponType.ThrustingOneHandSwords,
  WeaponType.Wands,
  WeaponType.Spears,
];

const RANGED_WEAPON_TYPES = [
  WeaponType.Bows,
  WeaponType.Crossbows,
];

const POE1_WEAPON_TYPES = [
  WeaponType.Bows,
  WeaponType.Claws,
  WeaponType.Daggers,
  WeaponType.OneHandSwords,
  WeaponType.OneHandAxes,
  WeaponType.OneHandMaces,
  WeaponType.Quivers,
  WeaponType.RuneDaggers,
  WeaponType.Sceptres,
  WeaponType.Staves,
  WeaponType.ThrustingOneHandSwords,
  WeaponType.TwoHandAxes,
  WeaponType.TwoHandMaces,
  WeaponType.TwoHandSwords,
  WeaponType.Wands,
  WeaponType.Warstaves,
];

const POE1_ATTACK_WEAPON_TYPES = [
  WeaponType.Bows,
  WeaponType.Claws,
  WeaponType.Daggers,
  WeaponType.OneHandAxes,
  WeaponType.OneHandMaces,
  WeaponType.OneHandSwords,
  WeaponType.ThrustingOneHandSwords,
  WeaponType.TwoHandAxes,
  WeaponType.TwoHandMaces,
  WeaponType.TwoHandSwords,
  WeaponType.Warstaves
];

const POE2_WEAPON_TYPES = [
  WeaponType.Bows,
  WeaponType.Crossbows,
  WeaponType.Foci,
  WeaponType.OneHandMaces,
  WeaponType.Quivers,
  WeaponType.Quarterstaves,
  WeaponType.Sceptres,
  WeaponType.Spears,
  WeaponType.Talismans,
  WeaponType.Staves,
  WeaponType.TwoHandMaces,
  WeaponType.Wands,
];

const POE2_ATTACK_WEAPON_TYPES = [
  WeaponType.Bows,
  WeaponType.Crossbows,
  WeaponType.Spears,
  WeaponType.Talismans,
  WeaponType.OneHandMaces,
  WeaponType.Quarterstaves,
  WeaponType.TwoHandMaces,
];

const POE1_ARMOUR_TYPES = [
  ArmourType.BodyArmours,
  ArmourType.Helmets,
  ArmourType.Gloves,
  ArmourType.Boots,
  ArmourType.Shields,
];

const POE2_ARMOUR_TYPES = [
  ArmourType.BodyArmours,
  ArmourType.Helmets,
  ArmourType.Gloves,
  ArmourType.Boots,
  ArmourType.Shields,
  ArmourType.Bucklers,
];

const POE2_JEWELS = [
  JewelType.Ruby,
  JewelType.Emerald,
  JewelType.Sapphire,
  JewelType.Diamond,
];

const POE1_JEWELS = [
  JewelType.Crimson,
  JewelType.Viridian,
  JewelType.Cobalt,
  JewelType.LargeCluster,
  JewelType.MediumCluster,
  JewelType.SmallCluster,
];

@Injectable({
  providedIn: 'root'
})
export class ItemDataService {
  private uniquesData = signal<UniqueItemDetails[]>([]);
  private gearData = signal<GearData[]>([]);
  private currencyData = signal<ItemDescription[]>([]);
  private jewelleryData = signal<JewelleryData[]>([]);
  private flaskData = signal<FlaskData[]>([]);
  private divinationCardsData = signal<DivCard[]>([]);
  private gemData = signal<GemData[]>([]);
  private modData = signal<Mod[]>([]);
  private questRewardsData = signal<QuestReward[]>([]);
  private gemRewardsData = signal<GemRewardQuest[]>([]);
  private vendorGemsData = signal<VendorGemEntry[]>([]);
  private zonesData = signal<Zone[]>([]);
  private gearValuesData = signal<GearValuesData[]>([]);
  private jewelleryValuesData = signal<JewelleryValuesData[]>([]);
  private ninjaBaseTypeUsageByBaseType = signal<Map<string, number>>(new Map());

  private allItemData!: ItemData[];
  private allRegularRunes!: string[];
  private currencyItemTypes!: string[];
  private tieredCurrencyItemTypes!: string[];
  private favouriteCurrencyItemTypes!: string[];
  private flatItemData!: FlatItemData[];
  private flatItemDataByBaseTypeName = new Map<string, FlatItemData>();
  private restrictedDropData: { type: string, content?: EndgameContent, baseType: string }[] = restrictedDropData.flatMap(r => r.baseTypes.map(b => ({ type: r.type, content: r.content, baseType: b })));

  private uniquesListData = [
    ...uniquesList.flatMap(u => u.items.map(i => parseUniqueListItem(i, u.category))),
    ...manualUniquesList.flatMap(u => u.items.map(i => parseUniqueListItem(i, u.category))),
  ];
  private uniqueBossDropEntries = uniqueBossDropsData;
  private uniqueBossDropsData = uniqueBossDropsData.flatMap(entry =>
    entry.baseTypes.map(drop => ({ unique: drop, bossName: entry.boss, content: entry.content }))
  );
  private classSpecificUniquesData = classSpecificUniques;

  private gearTierData = gearTierData;
  private jewelleryTierListData = jewelleryTierData;
  private equipmentData = equipmentData;
  private classPresetTexts = classPresetTexts;
  private classes = classes;
  private minimalCurrencyTierListData = minimalCurrencyTierListData;
  private minimalUniqueTierListData = minimalUniqueTierListData;
  private veryLowValueDivCards = veryLowValueDivCards;
  private lowValueDivCards = lowValueDivCards;
  private divCardRewardsByCardName = new Map<string, DivCardReward>();
  private divCardsByRewardName = new Map<string, string[]>();
  private currencyValuesFixed = currencyValuesFixed;
  private uniquesValuesFixed = uniqueValuesFixed;
  private currencyValuesManual = currencyValuesManual;
  private uniquesValuesManual = uniquesValuesManual;
  private tierListItemTypeManual = tierListItemTypeManual;
  private transfiguredGems = transfiguredGems;
  private areas = areas;
  private excellentClusterJewelBases = clusterJewelTiers.excellent;
  private goodClusterJewelBases = clusterJewelTiers.good;
  private clusterJewelPassiveNodes = clusterJewelEnchantments.flatMap(e => e.enchantments);

  private exportDateUniques = importDates.uniques;
  private exportDateCurrency = importDates.currency;

  private gearDefenceType = armourByDefenceType.flatMap(a => a.items.map(i => ({ name: i, defenceType: a.defenceType })));

  private defaultTieredMods!: TieredMod[];
  private modSlots!: ModSlot[];
  private modTags!: ModTag[];
  private modItemCategories!: ModItemCategory[];

  private modMatcherService!: ModMatcherService;

  constructor(private http: HttpClient, modMatcherService: ModMatcherService) {
    this.modMatcherService = modMatcherService;
    this.loadDivCardRewards();
    this.loadMetadata();
    this.loadItemData();
    this.loadModData();
  }

  private loadDivCardRewards() {
    for (const [cardName, encoded] of Object.entries(divCardRewardsData)) {
      const reward = decodeDivCardReward(encoded);
      this.divCardRewardsByCardName.set(cardName, reward);
      if (reward.disabled || !reward.name) continue;
      const cards = this.divCardsByRewardName.get(reward.name) ?? [];
      cards.push(cardName);
      this.divCardsByRewardName.set(reward.name, cards);
    }
    for (const cards of this.divCardsByRewardName.values()) {
      cards.sort((a, b) => a.localeCompare(b));
    }
  }

  private loadModData() {
    this.modTags = modTags;
    this.modItemCategories = modItemCategories;

    this.modSlots = modSlots.map(s => ({
      ...s,
      exampleBaseTypeImage: s.itemType ? this.allItemData.find(i => i.itemType === s.itemType)?.baseTypes[1].image : undefined
    } as ModSlot));

    this.defaultTieredMods = Object.entries(modTierData).flatMap(([itemCategory, tiers]) => tiers.flatMap((tier) => {
      return (tier.prefixes?.split('|').filter(p => p).map((m: string) => initMod(m, 'Prefix', tier.tier, itemCategory)) || [])
        .concat(tier.suffixes?.split('|').filter(s => s).map((m: string) => initMod(m, 'Suffix', tier.tier, itemCategory)) || [])
    }));

    function initMod(m: string, affixType: 'Prefix' | 'Suffix', tier: ModTierType, itemCategory: string) {
      return {
        name: m,
        affixType,
        cat: itemCategory as ModItemCategoryType,
        tier: tier
      } as TieredMod;
    }
  }

  private loadItemData() {
    const parseBaseTypes = (baseTypes: string[]) => baseTypes.map(b => ({ name: b.split('|')[0], image: b.split('|')[1] }));
    const existingItemTypes = new Set(itemData.map(i => i.itemType));
    const manualBaseTypesByItemType = manualItemData.reduce((map, m) => {
      map.set(m.itemType, [...(map.get(m.itemType) ?? []), m]);
      return map;
    }, new Map<string, typeof manualItemData>());

    this.allItemData = [
      ...itemData.map(i => ({
        ...i,
        baseTypes: parseBaseTypes(i.baseTypes).concat(
          (manualBaseTypesByItemType.get(i.itemType) ?? []).flatMap(m => parseBaseTypes(m.baseTypes))
        )
      })),
      ...Array.from(manualBaseTypesByItemType.entries())
        .filter(([itemType]) => !existingItemTypes.has(itemType))
        .map(([, entries]) => ({
          ...entries[0],
          baseTypes: entries.flatMap(e => parseBaseTypes(e.baseTypes))
        })),
    ].sort((a, b) => a.itemType.localeCompare(b.itemType));

    this.allItemData.forEach(item => {
      item.baseTypes.forEach(baseType => {
        baseType.image = item.itemClass === 'Divination Cards'
          ? '/img/gear/divination/divinationcard.webp'
          : baseType.image
            ? ((item.imageBaseUrl || '') + baseType.image)
            : undefined;

        const restrictedDrop = this.restrictedDropData.find(r => r.baseType === baseType.name);
        if (restrictedDrop) {
          baseType.restricted = restrictedDrop.type;
          baseType.restrictedContent = restrictedDrop.content;
        }
      });
    });

    this.allRegularRunes = this.allItemData.filter(i => i.itemType === 'Regular Runes').map(i => i.baseTypes.map(b => b.name)).flat();

    this.currencyItemTypes = Array.from(new Set(this.allItemData.filter(i => i.isCurrency).map(i => i.itemType))).concat(
      'Skill Gems', 'Support Gems', 'Gold',
      ...(poe2 ? ['All Augments', 'All Runes', 'All Gems', 'All Uncut Gems'] : []),
    );
    this.tieredCurrencyItemTypes = Array.from(new Set([
      'Currency',
      ...[
        ...this.allItemData.filter(i => (i.isCurrency && i.itemType != 'Currency' && i.itemType !== 'Expedition Logbooks') || i.itemType === 'Tablets').map(i => i.tierListItemType || i.itemType),
        'Expedition',
      ].sort(),
    ]));
    this.favouriteCurrencyItemTypes = Array.from(new Set(
      this.allItemData
        .filter(i => (i.isCurrency && i.itemType !== 'Currency' && i.itemType !== 'Expedition Logbooks') || i.itemType === 'Tablets')
        .map(i => i.itemType),
    )).sort();

    this.flatItemData = this.allItemData.map(item => {
      return item.baseTypes.map(baseType => ({
        itemType: item.itemType,
        itemClass: item.itemClass,
        baseType: baseType
      }));
    }).flat();
    this.flatItemDataByBaseTypeName = new Map(
      this.flatItemData.map(item => [item.baseType.name, item]),
    );
    if (isDevMode() && this.flatItemData.length !== this.flatItemDataByBaseTypeName.size) {
      console.warn('Duplicate base type names in flatItemData:', this.flatItemData.length - this.flatItemDataByBaseTypeName.size);
    }
  }

  private loadMetadata() {
    this.http.get<UniqueItemDetails[]>(`/data/${game}/uniques-data.json`).subscribe({
      next: (regularData) => {
        this.http.get<UniqueItemDetails[]>(`/data/${game}/uniques-data-manual.json`).subscribe({
          next: (manualData) => {
            const fullData = regularData.filter(d => !manualData.map(m => m.name).includes(d.name)).concat(manualData);
            this.uniquesData.set(normalizeUniqueDetails(fullData));
          },
          error: () => {
            this.uniquesData.set(normalizeUniqueDetails(regularData));
          }
        });
      },
      error: (error) => {
        console.error('Failed to load uniques data:', error);
      }
    });

    this.http.get<GearData[]>(`/data/${game}/gear-data.json`).subscribe({
      next: (data) => {
        this.gearData.set(data);
      },
      error: (error) => {
        console.error('Failed to load gear data:', error);
      }
    });

    this.http.get<JewelleryData[]>(`/data/${game}/jewellery-data.json`).subscribe({
      next: (data) => {
        this.jewelleryData.set(data);
      },
      error: (error) => {
        console.error('Failed to load jewellery data:', error);
      }
    });

    this.http.get<FlaskData[]>(`/data/${game}/flask-data.json`).subscribe({
      next: (data) => {
        this.flaskData.set(data);
      },
      error: (error) => {
        console.error('Failed to load flask data:', error);
      }
    });

    this.http.get<GearValuesData[]>(`/data/${game}/gear-values-data.json`).subscribe({
      next: (data) => {
        this.gearValuesData.set(data);
      },
      error: (error) => {
        console.error('Failed to load gear values data:', error);
      }
    });

    this.http.get<JewelleryValuesData[]>(`/data/${game}/jewellery-values-data.json`).subscribe({
      next: (data) => {
        this.jewelleryValuesData.set(data);
      },
      error: (error) => {
        console.error('Failed to load jewellery values data:', error);
      }
    });

    this.http.get<NinjaBaseTypesData>(`/data/${game}/ninja-base-types.json`).subscribe({
      next: (data) => {
        this.ninjaBaseTypeUsageByBaseType.set(this.flattenNinjaBaseTypeUsage(data));
      },
      error: (error) => {
        console.error('Failed to load ninja base types data:', error);
      }
    });

    // Load both regular and manual currency data asynchronously
    this.http.get<ItemDescription[]>(`/data/${game}/currency-data.json`).subscribe({
      next: (regularData) => {
        // After loading regular data, load manual data
        this.http.get<ItemDescription[]>(`/data/${game}/currency-data-manual.json`).subscribe({
          next: (manualData) => {
            // Merge data with manual data overriding existing entries
            const fullData = regularData
              .filter(d => !manualData.map(m => m.baseType).includes(d.baseType))
              .concat(manualData)
              .concat(descriptions);

            const heistLocationsData = heistLocations;
            const heistRewardsData = heistRewards;
            const syndicateMembersData = syndicateMembers;

            for (let location of heistLocationsData) {
              const locationData = {
                baseType: location.name,
                effects: location.skills.map(skill => `<img class="effect-icon" src="/img/heist/skills/${skill}.png" />: ${heistRewardsData.find(r => r.skill === skill)?.rewards.map(r => `<img class="effect-icon" src="/img/heist/rewards/${r}.png" />`).join(' ')}`)
              } as ItemDescription;

              fullData.push(locationData);
            }

            for (let member of syndicateMembersData) {
              const memberData = {
                baseType: member.name,
                effects: member.signatureMod.split('\n')
              } as ItemDescription;
              fullData.push(memberData);
            }

            this.currencyData.set(fullData);
          },
          error: (error) => {
            console.error('Failed to load manual currency data:', error);
            // Fallback to just regular data if manual fails
            this.processCurrencyData(regularData);
          }
        });
      },
      error: (error) => {
        console.error('Failed to load currency data:', error);
      }
    });

    this.http.get<DivCard[]>(`/data/${game}/div-card-data.json`).subscribe({
      next: (data) => {
        for (let manualCard of divinationCardsDataManual) {
          const existingCard = data.find(d => d.name === manualCard.name);
          if (!existingCard) {
            data.push(manualCard);
          }
        }
        this.divinationCardsData.set(data);
      },
      error: (error) => {
        console.error('Failed to load divination cards data:', error);
      }
    });

    this.http.get<GemData[]>(`/data/${game}/gem-data.json`).subscribe({
      next: (data) => {
        this.gemData.set(data);
      },
      error: (error) => {
        console.error('Failed to load gem data:', error);
      }
    });

    this.http.get<string[]>(`/data/${game}/mod-data.json`).subscribe({
      next: (rawData) => {
        const data = rawData.map(d => this.deserializeMod(d));
        this.modData.set(data);
        // Build pattern cache for mod matching
        this.modMatcherService.buildPatternCache(data);
      },
      error: (error) => {
        console.error('Failed to load mod data:', error);
      }
    });

    this.http.get<QuestReward[]>(`/data/${game}/quest-rewards.json`).subscribe({
      next: (data) => {
        this.questRewardsData.set(data);
      },
      error: (error) => {
        console.error('Failed to load quest rewards data:', error);
      }
    });

    this.http.get<GemRewardQuest[]>(`/data/${game}/gem-rewards.json`).subscribe({
      next: (data) => {
        this.gemRewardsData.set(data);
      },
      error: (error) => {
        console.error('Failed to load gem rewards data:', error);
      }
    });

    this.http.get<VendorGemEntry[]>(`/data/${game}/vendor-gems.json`).subscribe({
      next: (data) => {
        this.vendorGemsData.set(data);
      },
      error: (error) => {
        console.error('Failed to load vendor gems data:', error);
      }
    });

    this.http.get<Zone[]>(`/data/${game}/zones.json`).subscribe({
      next: (data) => {
        this.zonesData.set(data);
      },
      error: (error) => {
        console.error('Failed to load zones data:', error);
      }
    });
  }

  private processCurrencyData(regularData: ItemDescription[]) {
    const heistLocationsData = heistLocations;
    const heistRewardsData = heistRewards;
    const syndicateMembersData = syndicateMembers;

    for (let location of heistLocationsData) {
      const locationData = {
        baseType: location.name,
        effects: location.skills.map(skill => `<img class="effect-icon" src="/img/heist/skills/${skill}.png" />: ${heistRewardsData.find(r => r.skill === skill)?.rewards.map(r => `<img class="effect-icon" src="/img/heist/rewards/${r}.png" />`).join(' ')}`)
      } as ItemDescription;

      regularData.push(locationData);
    }

    for (let member of syndicateMembersData) {
      const memberData = {
        baseType: member.name,
        effects: member.signatureMod.split('\n')
      } as ItemDescription;
      regularData.push(memberData);
    }

    this.currencyData.set(regularData);
  }

  private deserializeMod(mod: string): Mod {
    const parts = mod.split('|');
    const idPart = parts[0];
    const domainPart = parts[1];
    const affixTypePart = parts[2];
    const typePart = parts[3];
    const lvlPart = parts[4];
    const namePart = parts[5];
    const textPart = parts[6];
    const slotsParts = parts[7].split(',').filter(s => s);
    const exSlotsParts = parts[8].split(',').filter(s => s);
    const tagsParts = parts[9].split(',').filter(s => s);

    return {
      id: parseInt(idPart),
      domain: domainPart as ModDomain,
      affixType: affixTypePart === 'p' ? 'Prefix' : 'Suffix' as AffixType,
      type: typePart.replace(/([A-Z])/g, ' $1').trim(),
      lvl: parseInt(lvlPart),
      name: namePart,
      text: textPart,
      slots: slotsParts.map(slot => this.modSlots.find(m => m.id === slot) || this.modSlots.find(m => m.techName === 'default')!),
      exSlots: exSlotsParts.map(slot => this.modSlots.find(m => m.id === slot) || this.modSlots.find(m => m.techName === 'default')!),
      tags: tagsParts.map(tag => this.modTags.find(mt => mt.id === tag) || { id: tag, name: tag, abbr: tag, color: 'tmt11', techName: tag }),
    } as Mod;
  }

  getItemData(): ItemData[] {
    return this.allItemData;
  }

  getAllRegularRunes() {
    return this.allRegularRunes;
  }

  getAllGemFilterClasses() {
    return '"Uncut Skill Gems" "Uncut Support Gems" "Uncut Spirit Gems" "Skill Gems" "Support Gems"';
  }

  getAllUncutGemFilterClasses() {
    return '"Uncut Skill Gems" "Uncut Support Gems" "Uncut Spirit Gems"';
  }

  getFlatItemData(): FlatItemData[] {
    return this.flatItemData;
  }

  getFlatItemDataByBaseTypeName(name: string): FlatItemData | undefined {
    return this.flatItemDataByBaseTypeName.get(name);
  }

  getManualCurrencyValues() {
    return this.currencyValuesManual;
  }

  getCurrencyValuesFixed() {
    return this.currencyValuesFixed;
  }

  getUniquesValuesFixed() {
    return this.uniquesValuesFixed;
  }

  getManualUniquesValues() {
    return this.uniquesValuesManual;
  }

  getLowValueDivCards() {
    return this.lowValueDivCards;
  }

  getVeryLowValueDivCards() {
    return this.veryLowValueDivCards;
  }

  // getCurrencyValues() {
  //   return this.currencyValues;
  // }

  // getPreviousCurrencyValues() {
  //   return this.previousCurrencyValues;
  // }

  // getUniqueValues() {
  //   return this.uniqueValues;
  // }

  // getPreviousUniqueValues() {
  //   return this.previousUniqueValues;
  // }

  getCurrencyItemTypes() {
    return this.currencyItemTypes;
  }

  isStackOnlyCurrencyItemType(itemType: string): boolean {
    return itemType === 'Gold' || (poe2 && itemType === 'Verisium');
  }

  isDesignerCurrencyItem(itemData?: { isCurrency: boolean; itemType: string }): boolean {
    return !!(itemData?.isCurrency || (itemData && this.isStackOnlyCurrencyItemType(itemData.itemType)));
  }

  getTieredCurrencyItemTypes() {
    return this.tieredCurrencyItemTypes;
  }

  getFavouriteCurrencyItemTypes() {
    return this.favouriteCurrencyItemTypes;
  }

  getScarabContentTypes(): EndgameContent[] {
    return scarabContentTypes;
  }

  getScarabBaseTypeToContent(): Record<string, EndgameContent[]> {
    return scarabBaseTypeToContent;
  }

  getScarabBaseTypesForContent(content: EndgameContent): string[] {
    return Object.entries(scarabBaseTypeToContent)
      .filter(([, contents]) => contents.includes(content))
      .map(([baseType]) => baseType);
  }

  // getCurrencyValue(baseType: string) {
  //   return this.currencyValues.find(v => v.name === baseType);
  // }

  // getUniqueValue(baseType: string) {
  //   return this.uniqueValues.find(v => v.name === baseType);
  // }

  // getPreviousCurrencyValue(baseType: string) {
  //   return this.previousCurrencyValues.find(v => v.name === baseType);
  // }

  // getPreviousUniqueValue(baseType: string) {
  //   return this.previousUniqueValues.find(v => v.name === baseType);
  // }

  getUniquesList() {
    return this.uniquesListData;
  }

  getCurrency(baseType: string) { // TODO: make this computed signal
    return this.currencyData().find(d => d.baseType === baseType);
  }

  getUnique(name: string) { // TODO: make this computed signal
    return this.uniquesData().find(u => u.name === name);
  }

  getDivinationCard(name: string) {
    return this.divinationCardsData().find(d => d.name === name);
  }

  getDivCardReward(cardName: string) {
    return this.divCardRewardsByCardName.get(cardName);
  }

  getDivCardRewardsByCardName() {
    return this.divCardRewardsByCardName;
  }

  getDivCardsByRewardName() {
    return this.divCardsByRewardName;
  }

  getDivCardsRewarding(itemName: string) {
    return this.divCardsByRewardName.get(itemName) ?? [];
  }

  getGear(name: string) { // TODO: make this computed signal
    return this.gearData().find(g => g.name === name);
  }

  getGearValues(baseType: string) {
    return this.gearValuesData().find(v => v.baseType === baseType);
  }

  getJewelleryValues(baseType: string) {
    return this.jewelleryValuesData().find(v => v.baseType === baseType);
  }

  getBaseTypeUsagePercentage(baseType: string): number | undefined {
    return this.ninjaBaseTypeUsageByBaseType().get(baseType);
  }

  private flattenNinjaBaseTypeUsage(data: NinjaBaseTypesData): Map<string, number> {
    const map = new Map<string, number>();
    for (const entries of Object.values(data.weapons)) {
      for (const entry of entries) {
        map.set(entry.baseType, entry.percentage);
      }
    }
    for (const slot of Object.values(data.armour)) {
      for (const entries of Object.values(slot)) {
        for (const entry of entries) {
          map.set(entry.baseType, entry.percentage);
        }
      }
    }
    return map;
  }

  getJewellery(name: string) { // TODO: make this computed signal
    return this.jewelleryData().find(d => d.name === name);
  }

  getFlask(name: string) { // TODO: make this computed signal
    return this.flaskData().find(d => d.name === name);
  }

  getUniqueBossDrops() {
    return this.uniqueBossDropsData;
  }

  getUniqueBossDropEntries(content?: EndgameContent) {
    return content
      ? this.uniqueBossDropEntries.filter(e => e.content === content)
      : this.uniqueBossDropEntries;
  }

  getItemTypesByContent(content: EndgameContent) {
    return this.allItemData
      .filter(i => i.content === content && i.isCurrency)
      .map(i => ({ itemType: i.itemType, image: i.baseTypes[0]?.image }));
  }

  getFavouriteCurrencyItemTypesByContent(content: EndgameContent): string[] {
    const favouriteTypes = new Set(this.getFavouriteCurrencyItemTypes());
    const types = new Set<string>();
    for (const item of this.allItemData.filter(i => i.content === content)) {
      if (favouriteTypes.has(item.itemType)) {
        types.add(item.itemType);
      }
    }
    return [...types].sort();
  }

  getUniqueBossDrop(name: string) {
    return this.uniqueBossDropsData.find(u => u.unique === name)?.bossName;
  }

  getUniqueBossDropContent(name: string) {
    return this.uniqueBossDropsData.find(u => u.unique === name)?.content;
  }

  getUniquesData() {
    return this.uniquesData;
  }

  getDivinationCardsData() {
    return this.divinationCardsData;
  }

  getGem(name: string) {
    return this.gemData().find(g => g.name === name);
  }

  getGemData() {
    return this.gemData;
  }

  getModData() {
    return this.modData;
  }

  getQuestRewardsData() {
    return this.questRewardsData;
  }

  getGemRewardsData() {
    return this.gemRewardsData;
  }

  getVendorGemsData() {
    return this.vendorGemsData;
  }

  getMod(id: number) {
    return this.modData().find(m => m.id === id);
  }

  getModMatcher(): ModMatcherService {
    return this.modMatcherService;
  }

  getGearData() {
    return this.gearData;
  }

  getClassSpecificUniques() {
    return this.classSpecificUniquesData;
  }

  getGearDefenceType() {
    return this.gearDefenceType;
  }

  getGearTierListData() {
    return this.gearTierData;
  }

  getJewelleryTierListData() {
    return this.jewelleryTierListData;
  }

  getEquipmentData() {
    return this.equipmentData;
  }

  getClassPresetTexts() {
    return this.classPresetTexts;
  }

  getClasses() {
    return this.classes;
  }

  getMinimalCurrencyTierListData() {
    return this.minimalCurrencyTierListData;
  }

  getMinimalUniqueTierListData() {
    return this.minimalUniqueTierListData;
  }

  getTierListItemTypeManual() {
    return this.tierListItemTypeManual;
  }

  getTierListItemTypeContent(tierListItemType: string): EndgameContent | undefined {
    return this.allItemData.find(i => (i.tierListItemType || i.itemType) === tierListItemType && i.content)?.content;
  }

  getExportDateUniques() {
    return this.exportDateUniques;
  }

  getExportDateCurrency() {
    return this.exportDateCurrency;
  }

  getTransfiguredGems() {
    return this.transfiguredGems;
  }

  getZonesData() {
    return this.zonesData;
  }

  getAreas() {
    return this.areas;
  }

  getTieredMods() {
    return this.defaultTieredMods;
  }

  getModSlots() {
    return this.modSlots;
  }

  getModTags() {
    return this.modTags;
  }

  getModItemCategories() {
    return this.modItemCategories;
  }

  getHidableCurrencyTypes() {
    return currencyTypesToHide;
  }

  getNotDroppableCurrencyTypes() {
    return currencyTypesNotDroppable;
  }

  getRuthlessDisabledCurrencyItems() {
    return ruthlessDisabledCurrencyItems;
  }

  getRuthlessDisabledCurrencyTypes() {
    return ruthlessDisabledCurrencyTypes;
  }

  getRuthlessOnlyCurrencyItems() {
    return ruthlessOnlyCurrencyItems;
  }

  getAllSyndicateMembers() {
    return syndicateMembers;
  }

  getExcellentClusterJewelBases() {
    return this.excellentClusterJewelBases;
  }

  getGoodClusterJewelBases() {
    return this.goodClusterJewelBases;
  }

  getClusterJewelPassiveNodes() {
    return this.clusterJewelPassiveNodes;
  }

  getAllGearTypes() {
    return [...this.getAllWeaponTypes(), ...this.getAllArmourTypes()];
  }

  getAllJewelClasses() {
    return poe1 ? '"Jewels" "Abyss Jewels"' : '"Jewels"';
  }

  getAllJewelleryClassesWithoutTalismans() {
    return '"Rings" "Amulets" "Belts"';
  }

  isJewelleryType(itemClass: string) {
    return this.getAllJewelleryClassesWithoutTalismans().includes(itemClass);
  }

  getAllWeaponTypes(includeAll = false) {
    return (includeAll ? [WeaponType.All] : [])
      .concat(poe2 ? POE2_WEAPON_TYPES : POE1_WEAPON_TYPES)
      .map(w => w.toString());
  }

  getOneHandWeaponTypes() {
    return (poe2 ? POE2_WEAPON_TYPES : POE1_WEAPON_TYPES).filter(w => ONE_HAND_WEAPON_TYPES.includes(w));
  }

  getTwoHandedWeaponTypes() {
    return (poe2 ? POE2_WEAPON_TYPES : POE1_WEAPON_TYPES).filter(w => !ONE_HAND_WEAPON_TYPES.includes(w) && ![WeaponType.Foci, WeaponType.Quivers].includes(w));
  }

  getAttackWeaponTypes() {
    return poe1? POE1_ATTACK_WEAPON_TYPES : POE2_ATTACK_WEAPON_TYPES;
  }

  getRangedWeaponTypes() {
    return RANGED_WEAPON_TYPES;
  }

  getAllArmourTypes(includeAll = false) {
    return (includeAll ? [ArmourType.All] : [])
      .concat(poe2 ? POE2_ARMOUR_TYPES : POE1_ARMOUR_TYPES)
      .map(g => g.toString());
  }

  getHardCodedChanceBases() {
    return hardCodedChanceBases;
  }

  getExcludedChanceBaseItemClasses() {
    return excludedChanceBaseItemClasses;
  }

  // getAllArmourTypesButShields() {
  //   return [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots].map(g => g.toString());
  // }

  getClassesWithDefenceTypes() {
    return ['Body Armours', 'Shields', 'Helmets', 'Gloves', 'Boots'];
  }

  getCantHaveQuality() {
    return cantHaveQualityItemClasses;
  }

  getCantHaveSockets() {
    return cantHaveSocketsItemClasses;
  }

  getCanBeIdentified() {
    return this.getAllArmourTypes().concat(this.getAllWeaponTypes()).concat(['Belts', 'Amulets', 'Rings',
      'Jewels', 'Time Lost Jewels', 'Abyss Jewels',
      'Life Flasks', 'Mana Flasks', 'Hybrid Flasks', 'Utility Flasks', 'Tinctures', 'Charms',
      'Waystones', 'Maps', 'Tablets', 'Expedition Logbooks',
      'Relics']);
  }

  getRareOnlyJewellery = () => rareOnlySpecialJewellery;
  getMinimalCharms = () => minimalCharms;
  getCheapCurrency = () => cheapCurrency;
  getStackableCurrency = () => stackableCurrency;
  getRestrictedDropTypesToTreatAsRegular = () => restrictedDropTypesToTreatAsRegular;
  getNonDroppableUniqueBossTypes = () => nonDroppableUniqueBossTypes;
  getSpecialGearBaseTypes(): string[] {
    return this.restrictedDropData
      .map(r => r.baseType)
      .filter(bt => !bt.includes('Ring') && !bt.includes('Amulet') && !bt.includes('Belt') && !bt.includes('Talisman'));
  }

  getSpecialJewelleryBaseTypes(): string[] {
    return this.restrictedDropData
      .map(r => r.baseType)
      .filter(bt => bt.includes('Ring') || bt.includes('Amulet') || bt.includes('Belt') || bt.includes('Talisman'));
  }
  getItemColors = () => itemColors;

  getAllJewelTypes(includeAll = false) {
    return (includeAll ? [JewelType.All] : [])
      .concat(poe2 ? POE2_JEWELS : POE1_JEWELS)
      .map(j => j.toString());
  }

  getAllFlaskTypes() {
    return poe1 ? ['Life Flasks', 'Mana Flasks', 'Hybrid Flasks', 'Utility Flasks', 'Tinctures'] : ['Life Flasks', 'Mana Flasks', 'Charms'];
  }

  getInfluences() {
    return poe2 ? [] : [
      { name: 'Shaper', image: '/img/icons/influences/shaper.png' },
      { name: 'Elder', image: '/img/icons/influences/elder.png' },
      { name: 'Crusader', image: '/img/icons/influences/crusader.png' },
      { name: 'Hunter', image: '/img/icons/influences/hunter.png' },
      { name: 'Redeemer', image: '/img/icons/influences/redeemer.png' },
      { name: 'Warlord', image: '/img/icons/influences/warlord.png' }
    ];
  }

  getAllDefenceTypes() {
    return [
      { name: 'Arm', image: '/img/shields/shield-red.png' },
      { name: 'Eva', image: '/img/shields/shield-green.png' },
      { name: 'ES', image: '/img/shields/shield-blue.png' },
      { name: 'Arm/Eva', image: '/img/shields/shield-red-green.png' },
      { name: 'Arm/ES', image: '/img/shields/shield-red-blue.png' },
      { name: 'Eva/ES', image: '/img/shields/shield-green-blue.png' }
    ];
  }

  getPreset(preset: Presets) {
    switch (preset) {
      case 'presetEarlyCampaign1':
        return presetEarlyCampaign1;
      case 'presetMidCampaign30':
        return presetMidCampaign30;
      case 'presetLateCampaignEarlyMaps60':
        return presetLateCampaignEarlyMaps60;
      case 'presetMidMaps70':
        return presetMidMaps70;
      case 'presetEndGame80':
        return presetEndGame80;
      case 'presetStrict90':
        return presetStrict90;
      case 'presetVeryStrict95':
        return presetVeryStrict95;
      default:
        throw new Error(`Unknown preset: ${preset}`);
    }
  }

  getRuthlessPreset(preset: Presets) {
    switch (preset) {
      case 'presetEarlyCampaign1':
        return ruthlessPresetEarlyCampaign1;
      case 'presetMidCampaign30':
        return ruthlessPresetMidCampaign30;
      case 'presetLateCampaignEarlyMaps60':
        return ruthlessPresetLateCampaignEarlyMaps60;
      case 'presetMidMaps70':
        return ruthlessPresetMidMaps70;
      case 'presetEndGame80':
        return ruthlessPresetEndGame80;
      case 'presetStrict90':
        return ruthlessPresetStrict90;
      case 'presetVeryStrict95':
        return ruthlessPresetVeryStrict95;
      default:
        throw new Error(`Unknown preset: ${preset}`);
    }
  }
}
